// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the one line that says this device is in a shared session, and
 * as what: leading it, following it, or still connecting
 * (`docs/SHARED_AR_PLAN.md`). With the number of people in the room,
 * because "is anyone else here?" is the first question either side has.
 *
 * It is also the way to invite more people: a tap opens the invitations
 * (meetingPanel's `openMeetingSharePanel`), so a presenter who has
 * already stepped into the seat can still send the links.
 *
 * Mounted into the WebXR overlay root, which is on screen both in the
 * ordinary page and over the camera picture in handheld AR, so the same
 * element serves both.
 */

import { t } from '../i18n'
import type { RoomStatus } from '../services/roomClient'
import { formatRoomCode } from './meetingShare'

/** The chip's wording for a status. */
export function roomChipText(status: RoomStatus): string {
  // AYNI: grouped as people read it out and type it (owner, 2026-10-05: 5-5).
  const params = { code: formatRoomCode(status.code), count: status.count }
  if (!status.connected) return t('room.chip.connecting', params)
  if (status.meeting) {
    // A meeting with nobody presenting is a lobby: say so, to every seat.
    if (!status.hasLead) return t('meeting.chip.waiting', params)
    // By seat, not by who leads: the presenter still presents while a
    // participant has the globe, and that participant is not presenting.
    if (status.seat === 'presenter') return t('meeting.chip.presenter', params)
    if (status.spotlight?.me) return t('meeting.chip.spotlight', params)
    // AYNI (multi-globe plan, phase 3): given one globe of several.
    if (status.driving !== null) return t('meeting.chip.driving', { ...params, n: status.driving + 1 })
    return t(status.seat === 'moderator' ? 'meeting.chip.moderator' : 'meeting.chip.audience', params)
  }
  if (status.role === null) return t('room.chip.connecting', params)
  return t(status.role === 'lead' ? 'room.chip.lead' : 'room.chip.follower', params)
}

/**
 * Mount the chip under `root` and keep it current from `subscribe`
 * (`RoomSyncHandle.onStatus`). `onInvite` is called with the chip itself
 * (to take the focus back) and the latest status when it is tapped.
 * Returns its teardown.
 */
export function mountRoomChip(
  root: HTMLElement,
  subscribe: (listener: (status: RoomStatus) => void) => () => void,
  onInvite?: (chip: HTMLElement, status: RoomStatus) => void,
): () => void {
  const chip = document.createElement('button')
  chip.type = 'button'
  chip.className = 'room-chip'
  const label = document.createElement('span')
  label.setAttribute('role', 'status')
  const hint = document.createElement('span')
  hint.className = 'room-chip-invite'
  hint.textContent = t('room.chip.invite')
  chip.append(label, hint)
  chip.title = t('room.chip.invite.title')
  // A tap here is for the page, not for the XR session behind it.
  chip.addEventListener('beforexrselect', (ev) => ev.preventDefault())
  root.appendChild(chip)
  let latest: RoomStatus | null = null
  chip.addEventListener('click', () => {
    if (latest && onInvite) onInvite(chip, latest)
  })
  const unsubscribe = subscribe((status) => {
    latest = status
    label.textContent = roomChipText(status)
    chip.dataset.role = status.connected && status.role ? status.role : 'connecting'
  })
  return () => {
    unsubscribe()
    chip.remove()
  }
}
